import { Placeholder } from "@tiptap/extensions"
import { type EditorState, Plugin } from "@tiptap/pm/state"
import { AddMarkStep } from "@tiptap/pm/transform"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import {
	type Editor,
	EditorContent,
	Extension,
	Mark,
	markInputRule,
	markPasteRule,
	useEditor,
	useEditorState,
} from "@tiptap/react"
import { BubbleMenu } from "@tiptap/react/menus"
import StarterKit from "@tiptap/starter-kit"
import "@/shared/lib/spoiler"
import {
	Bold,
	Code,
	Eye,
	Italic,
	Link,
	type LucideIcon,
	Quote,
	Strikethrough,
	Underline,
	Unlink,
} from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react"
import { TRANSITION } from "@/shared/lib/motion"
import {
	normalizeLinkInput,
	parseRichText,
	type RichTextFormat,
	serializeRichText,
} from "@/shared/lib/rich-text"
import { cn } from "@/shared/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"

const MOD = typeof navigator !== "undefined" && /Mac/.test(navigator.platform) ? "⌘" : "Ctrl+"

/** Ввод в духе Telegram: ||спойлер||. */
const spoilerInput = /(?:^|\s)(\|\|(?!\s+\|\|)((?:[^|]+))\|\|(?!\s+\|\|))$/
const spoilerPaste = /(?:^|\s)(\|\|(?!\s+\|\|)((?:[^|]+))\|\|(?!\s+\|\|))/g
/** __курсив__ как в Telegram (в стандартном наборе это был бы жирный). */
const italicInput = /(?:^|\s)(__(?!\s+__)((?:[^_]+))__(?!\s+__))$/
/** ++подчёркнутый++, как в разметке постов. */
const underlineInput = /(?:^|\s)(\+\+(?!\s+\+\+)((?:[^+]+))\+\+(?!\s+\+\+))$/

const Spoiler = Mark.create({
	name: "spoiler",
	parseHTML: () => [{ tag: "tg-spoiler" }, { tag: "span.tg-spoiler" }, { tag: "spoiler-span" }],
	// redraw меняется, когда открытый спойлер пора спрятать: новый элемент снова скрыт.
	addAttributes: () => ({
		redraw: {
			default: 0,
			parseHTML: () => 0,
			renderHTML: (attrs) => ({ "data-redraw": attrs.redraw }),
		},
	}),
	renderHTML: ({ HTMLAttributes }) => ["spoiler-span", { ...HTMLAttributes, density: "5" }, 0],
	addKeyboardShortcuts() {
		return { "Mod-Shift-p": () => this.editor.commands.toggleMark(this.name) }
	},
	addInputRules() {
		return [markInputRule({ find: spoilerInput, type: this.type })]
	},
	addPasteRules() {
		return [markPasteRule({ find: spoilerPaste, type: this.type })]
	},
})

/** Сочетания из Telegram и быстрый ввод, которых нет в стандартном наборе. */
const TelegramShortcuts = Extension.create({
	name: "telegramShortcuts",
	priority: 1000,
	addKeyboardShortcuts() {
		return {
			"Mod-Shift-x": () => this.editor.commands.toggleStrike(),
			"Mod-Shift-m": () => this.editor.commands.toggleCode(),
		}
	},
	addInputRules() {
		const { italic, underline } = this.editor.schema.marks
		return [
			...(italic ? [markInputRule({ find: italicInput, type: italic })] : []),
			...(underline ? [markInputRule({ find: underlineInput, type: underline })] : []),
		]
	},
})

/** ${переменные} подсвечены, чтобы их не принимали за опечатку и не ломали. */
/**
 * Пробел по краю только что отмеченного куска остаётся без отметки: `**слово **` не
 * разметка. Двойной щелчок выделяет слово вместе с пробелом после него, и без этого
 * спойлер или жирный захватывали бы и пробел. Пробел внутри одного формата не трогаем.
 */
const TrimMarkEdges = Extension.create({
	name: "trimMarkEdges",
	addProseMirrorPlugins: () => [
		new Plugin({
			appendTransaction: (transactions, _old, state: EditorState) => {
				const added = transactions
					.flatMap((transaction) => transaction.steps)
					.filter((step): step is AddMarkStep => step instanceof AddMarkStep)
				if (added.length === 0) return null
				const tr = state.tr
				const isSpace = (pos: number) => /\s/.test(state.doc.textBetween(pos, pos + 1, "\n", "\n"))
				for (const step of added) {
					const { from, to, mark } = step
					let start = from
					while (start < to && isSpace(start)) start++
					let end = to
					while (end > start && isSpace(end - 1)) end--
					if (start > from && !(from > 0 && state.doc.rangeHasMark(from - 1, from, mark)))
						tr.removeMark(from, start, mark)
					if (
						end < to &&
						!(to < state.doc.content.size && state.doc.rangeHasMark(to, to + 1, mark))
					)
						tr.removeMark(end, to, mark)
				}
				return tr.docChanged ? tr : null
			},
		}),
	],
})

const VariableHighlight = Extension.create({
	name: "variableHighlight",
	addProseMirrorPlugins() {
		return [
			new Plugin({
				props: {
					decorations(state) {
						const decorations: Decoration[] = []
						state.doc.descendants((node, pos) => {
							if (!node.isText || !node.text) return
							for (const match of node.text.matchAll(/\$\{[^}\s]+\}/g)) {
								const from = pos + (match.index ?? 0)
								decorations.push(
									Decoration.inline(from, from + match[0].length, { class: "rich-var" }),
								)
							}
						})
						return DecorationSet.create(state.doc, decorations)
					},
				},
			}),
		]
	},
})

interface FormatButton {
	mark: string
	label: string
	icon: LucideIcon
	hotkey?: string
	run: (editor: Editor) => void
}

const BUTTONS: FormatButton[] = [
	{
		mark: "bold",
		label: "Жирный",
		icon: Bold,
		hotkey: "B",
		run: (e) => e.chain().focus().toggleBold().run(),
	},
	{
		mark: "italic",
		label: "Курсив",
		icon: Italic,
		hotkey: "I",
		run: (e) => e.chain().focus().toggleItalic().run(),
	},
	{
		mark: "underline",
		label: "Подчёркнутый",
		icon: Underline,
		hotkey: "U",
		run: (e) => e.chain().focus().toggleUnderline().run(),
	},
	{
		mark: "strike",
		label: "Зачёркнутый",
		icon: Strikethrough,
		hotkey: "Shift+X",
		run: (e) => e.chain().focus().toggleStrike().run(),
	},
	{
		mark: "code",
		label: "Моноширинный",
		icon: Code,
		hotkey: "Shift+M",
		run: (e) => e.chain().focus().toggleCode().run(),
	},
	{
		mark: "spoiler",
		label: "Спойлер",
		icon: Eye,
		hotkey: "Shift+P",
		run: (e) => e.chain().focus().toggleMark("spoiler").run(),
	},
	{
		mark: "blockquote",
		label: "Цитата",
		icon: Quote,
		hotkey: "Shift+B",
		run: (e) => e.chain().focus().toggleBlockquote().run(),
	},
]

function useActiveMarks(editor: Editor | null) {
	return (
		useEditorState({
			editor,
			selector: ({ editor: current }) =>
				current
					? Object.fromEntries(
							[...BUTTONS.map((button) => button.mark), "link"].map((mark) => [
								mark,
								current.isActive(mark),
							]),
						)
					: {},
		}) ?? {}
	)
}

function ToolButton({
	label,
	hotkey,
	icon: Icon,
	active,
	onClick,
}: {
	label: string
	hotkey?: string
	icon: LucideIcon
	active?: boolean
	onClick: () => void
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					aria-label={label}
					aria-pressed={active}
					onMouseDown={(event) => event.preventDefault()}
					onClick={onClick}
					className={cn(
						// --accent в тёмной теме совпадает с фоном меню: подсветка от цвета текста.
						"grid size-8 place-items-center rounded-lg transition-colors duration-150 hover:bg-foreground/10 hover:text-foreground",
						active ? "bg-foreground/15 text-foreground" : "text-muted-foreground",
					)}
				>
					<Icon className="size-4" />
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">
				{label}
				{hotkey ? (
					<span className="ml-2 text-muted-foreground">
						{MOD}
						{hotkey}
					</span>
				) : null}
			</TooltipContent>
		</Tooltip>
	)
}

function FormatButtons({
	editor,
	onLink,
	onPicked,
}: {
	editor: Editor
	onLink: () => void
	/** Формат выбран: меню убирается, пока не выделят другой текст. */
	onPicked: () => void
}) {
	const active = useActiveMarks(editor)
	return (
		<>
			{BUTTONS.map((button) => (
				<ToolButton
					key={button.mark}
					label={button.label}
					hotkey={button.hotkey}
					icon={button.icon}
					active={active[button.mark]}
					onClick={() => {
						onPicked()
						button.run(editor)
					}}
				/>
			))}
			<ToolButton label="Ссылка" hotkey="K" icon={Link} active={active.link} onClick={onLink} />
		</>
	)
}

/** Поле адреса ссылки прямо в меню над текстом: Enter применяет, Esc отменяет. */
function LinkForm({ editor, onDone }: { editor: Editor; onDone: () => void }) {
	const [href, setHref] = useState(
		() => (editor.getAttributes("link").href as string | undefined) ?? "",
	)
	const [invalid, setInvalid] = useState(false)
	const input = useRef<HTMLInputElement>(null)
	// Меню появляется после того, как редактор обработал нажатие, и фокус надо забрать явно.
	useEffect(() => {
		const timer = window.setTimeout(() => input.current?.focus(), 0)
		return () => window.clearTimeout(timer)
	}, [])
	const apply = () => {
		const url = normalizeLinkInput(href)
		if (!href.trim()) {
			editor.chain().focus().extendMarkRange("link").unsetLink().run()
			onDone()
			return
		}
		if (!url) {
			setInvalid(true)
			return
		}
		const chain = editor.chain().focus().extendMarkRange("link")
		if (editor.state.selection.empty && !editor.isActive("link"))
			chain.insertContent({
				type: "text",
				text: url,
				marks: [{ type: "link", attrs: { href: url } }],
			})
		else chain.setLink({ href: url })
		chain.run()
		onDone()
	}
	return (
		<form
			className="flex items-center gap-1 p-1"
			onSubmit={(event) => {
				event.preventDefault()
				apply()
			}}
		>
			<input
				ref={input}
				aria-label="Адрес ссылки"
				aria-invalid={invalid}
				placeholder="https://"
				value={href}
				onChange={(event) => {
					setHref(event.target.value)
					setInvalid(false)
				}}
				onKeyDown={(event) => {
					if (event.key === "Escape") {
						event.preventDefault()
						editor.commands.focus()
						onDone()
					}
				}}
				className={cn(
					"h-8 w-56 rounded-md border bg-background px-2 text-sm outline-none",
					invalid ? "border-destructive" : "border-input",
				)}
			/>
			<button
				type="submit"
				className="h-8 rounded-md px-2 text-sm transition-colors hover:bg-foreground/10"
			>
				Готово
			</button>
			{editor.isActive("link") ? (
				<button
					type="button"
					aria-label="Убрать ссылку"
					className="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
					onClick={() => {
						editor.chain().focus().extendMarkRange("link").unsetLink().run()
						onDone()
					}}
				>
					<Unlink className="size-4" />
				</button>
			) : null}
		</form>
	)
}

/**
 * Поле текста с форматированием, которое видно сразу, как в Telegram или Notion: жирный
 * выглядит жирным, а не обёрнутым в звёздочки. Работают сочетания из Telegram (⌘B, ⌘I,
 * ⌘U, ⌘K, ⌘⇧X, ⌘⇧M, ⌘⇧P), быстрый ввод (**жирный**, __курсив__, ~~зачёркнутый~~,
 * `код`, ||спойлер||, «> » для цитаты) и меню над выделенным текстом. Хранится текст в
 * прежнем формате: HTML Telegram у бота и рассылок, разметка у постов.
 */
export function RichTextEditor({
	value,
	onChange,
	format,
	placeholder,
	id,
	ariaLabel,
	minHeight = "6rem",
	editorRef,
	className,
	invalid,
	footer,
	contentClassName,
}: {
	value: string
	onChange: (value: string) => void
	format: RichTextFormat
	placeholder?: string
	id?: string
	ariaLabel?: string
	minHeight?: string
	/** Для вставки снаружи: переменные, эмодзи. */
	editorRef?: RefObject<Editor | null>
	className?: string
	invalid?: boolean
	/** Под текстом справа: например, счётчик символов. */
	footer?: ReactNode
	/** Шрифт и отступы самого текста. */
	contentClassName?: string
}) {
	const last = useRef(value)
	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange
	const [linkOpen, setLinkOpen] = useState(false)
	const linkOpenRef = useRef(false)
	linkOpenRef.current = linkOpen
	// Видимость меню над текстом ведём сами: плагин вставляет и убирает его мгновенно, а
	// нам нужно проявление и затухание. Пока меню гаснет, плагин держит его на странице,
	// а позиция замирает, чтобы оно не уехало за курсором.
	const [menuShown, setMenuShown] = useState(false)
	const menuShownRef = useRef(false)
	const exiting = useRef<{ left: string; top: string } | null>(null)
	const exitTimer = useRef(0)
	// Выделение, к которому уже применили формат: на нём меню больше не показываем.
	const dismissed = useRef<{ from: number; to: number } | null>(null)
	const menuElement = useRef<HTMLDivElement>(null)
	const showMenu = (next: boolean) => {
		if (menuShownRef.current === next) return
		menuShownRef.current = next
		setMenuShown(next)
	}
	// Меню гаснет на месте, а убирает его плагин уже после затухания.
	const fadeOut = () => {
		const element = menuElement.current?.parentElement
		if (!element || !menuShownRef.current) return
		exiting.current = { left: element.style.left, top: element.style.top }
		showMenu(false)
		window.clearTimeout(exitTimer.current)
		exitTimer.current = window.setTimeout(() => {
			exiting.current = null
			if (editor && !editor.isDestroyed)
				editor.view.dispatch(editor.state.tr.setMeta("bubbleMenuExit", true))
		}, 180)
	}

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: false,
				bulletList: false,
				orderedList: false,
				listItem: false,
				listKeymap: false,
				horizontalRule: false,
				trailingNode: false,
				link: {
					openOnClick: false,
					autolink: false,
					linkOnPaste: true,
					HTMLAttributes: { rel: null, target: null },
					isAllowedUri: (url) => !!normalizeLinkInput(url),
				},
			}),
			Spoiler,
			TelegramShortcuts,
			TrimMarkEdges,
			VariableHighlight,
			Extension.create({
				name: "linkShortcut",
				addKeyboardShortcuts: () => ({
					"Mod-k": () => {
						setLinkOpen(true)
						return true
					},
				}),
			}),
			Placeholder.configure({ placeholder: placeholder ?? "" }),
		],
		content: parseRichText(value, format),
		shouldRerenderOnTransaction: false,
		editorProps: {
			attributes: {
				class: cn("rich-text px-3 py-2 text-base md:text-sm", contentClassName),
				style: `min-height: ${minHeight}`,
				...(id ? { id } : {}),
				...(ariaLabel ? { "aria-label": ariaLabel } : {}),
				"aria-multiline": "true",
				role: "textbox",
			},
		},
		onUpdate: ({ editor: current }) => {
			const next = serializeRichText(current.getJSON(), format)
			if (next === last.current) return
			last.current = next
			onChangeRef.current(next)
		},
	})

	useEffect(() => {
		if (editorRef) editorRef.current = editor
	}, [editor, editorRef])

	// Меню над текстом пересчитывает, показываться ли, только на транзакции.
	useEffect(() => {
		if (editor && !editor.isDestroyed)
			editor.view.dispatch(editor.state.tr.setMeta("linkForm", linkOpen))
	}, [editor, linkOpen])

	// Значение сменили снаружи (другой черновик, откат): показываем его, не считая правкой.
	useEffect(() => {
		if (!editor || value === last.current) return
		last.current = value
		editor.commands.setContent(parseRichText(value, format), { emitUpdate: false })
	}, [editor, value, format])

	if (!editor) return null

	return (
		<div
			className={cn(
				"w-full rounded-xl border border-input bg-secondary/40 transition-[border-color,box-shadow] duration-150 focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/25",
				invalid && "border-destructive",
				className,
			)}
		>
			<BubbleMenu
				editor={editor}
				options={{
					placement: "top-start",
					offset: 8,
					flip: true,
					shift: { padding: 8 },
					onUpdate: () => {
						const frozen = exiting.current
						const element = menuElement.current?.parentElement
						if (frozen && element) {
							element.style.left = frozen.left
							element.style.top = frozen.top
						}
					},
				}}
				shouldShow={({ state }) => {
					const { from, to, empty } = state.selection
					const stale = dismissed.current?.from === from && dismissed.current?.to === to
					if (!stale) dismissed.current = null
					const wanted = linkOpenRef.current || (!empty && editor.isFocused && !stale)
					if (wanted) {
						window.clearTimeout(exitTimer.current)
						exiting.current = null
						showMenu(true)
						return true
					}
					if (menuShownRef.current) fadeOut()
					return exiting.current !== null
				}}
				className="z-50"
			>
				<div
					ref={menuElement}
					className={cn(
						"flex origin-bottom-left items-center gap-0.5 rounded-xl border border-border bg-popover p-1 shadow-lg transition-[opacity,transform] duration-150 ease-[var(--ease-out-soft)]",
						menuShown ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0",
					)}
				>
					{/* Кнопки форматирования и поле ссылки сменяют друг друга перекрёстно. */}
					<AnimatePresence initial={false} mode="popLayout">
						<motion.div
							key={linkOpen ? "link" : "format"}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1, transition: TRANSITION.base }}
							exit={{ opacity: 0, transition: TRANSITION.fast }}
							className="flex items-center gap-0.5"
						>
							{linkOpen ? (
								<LinkForm
									editor={editor}
									onDone={() => {
										const { from, to } = editor.state.selection
										dismissed.current = { from, to }
										setLinkOpen(false)
									}}
								/>
							) : (
								<FormatButtons
									editor={editor}
									onLink={() => setLinkOpen(true)}
									onPicked={() => {
										const { from, to } = editor.state.selection
										dismissed.current = { from, to }
										// Плагин пересчитывает меню с задержкой: гасим сразу по клику.
										fadeOut()
									}}
								/>
							)}
						</motion.div>
					</AnimatePresence>
				</div>
			</BubbleMenu>
			<EditorContent editor={editor} />
			{footer ? <div className="flex justify-end px-3 pb-2">{footer}</div> : null}
		</div>
	)
}
